/**
 * AppErrorBoundary — last-resort fallback for crashes ABOVE the providers.
 *
 * The per-route `RouteErrorBoundary` is mounted inside the AuthProvider /
 * QueryClientProvider / ThemeProvider tree, so a crash in any of those
 * (Supabase init failure, env-var typo, theme provider corrupt state, etc.)
 * blows past it and white-screens the app.
 *
 * This boundary sits at the very top of `App.tsx`. It deliberately uses
 * NO providers, NO context, NO design-system components — just inline
 * styles — so a corrupt provider tree can't take down the fallback as
 * well. Looks plain, but plain-and-working beats branded-and-broken.
 *
 * Recovery is a hard reload (location.reload). We don't try to re-mount
 * the children because if a provider failed to initialise, re-mounting
 * will hit the same failure on the next render.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportError } from "@/sentry";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportError(error, {
      source: "app_error_boundary",
      extra:  { componentStack: info.componentStack },
    });
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    // Inline styles only — we cannot rely on Tailwind tokens being live
    // here (e.g. a stylesheet load failure could be the very thing that
    // tripped the boundary).
    return (
      <div
        role="alert"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "32px 20px",
          background: "#fafaf7",
          fontFamily:
            "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          color: "#1a1a1a",
        }}
      >
        <div
          style={{
            maxWidth: 480,
            width: "100%",
            background: "#ffffff",
            border: "1px solid #e5e5e0",
            borderRadius: 12,
            padding: "28px 28px 24px",
            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
          }}
        >
          <div
            style={{
              fontSize: 11,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "#8a8a82",
              marginBottom: 8,
            }}
          >
            Something went wrong
          </div>
          <h1
            style={{
              fontFamily:
                "'Instrument Serif', ui-serif, Georgia, serif",
              fontSize: 28,
              lineHeight: 1.15,
              margin: "0 0 12px",
              fontWeight: 400,
            }}
          >
            RouteFlow couldn't load this page.
          </h1>
          <p
            style={{
              fontSize: 14,
              lineHeight: 1.55,
              color: "#4a4a44",
              margin: "0 0 20px",
            }}
          >
            A reload usually fixes it. If it keeps happening, your session
            may have expired — sign in again from the auth page.
          </p>
          <details
            style={{
              fontSize: 12,
              color: "#6b6b63",
              marginBottom: 20,
              cursor: "pointer",
            }}
          >
            <summary style={{ outline: "none" }}>Technical details</summary>
            <pre
              style={{
                marginTop: 8,
                padding: 10,
                background: "#f5f5f0",
                border: "1px solid #e5e5e0",
                borderRadius: 6,
                fontFamily:
                  "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, monospace",
                fontSize: 11.5,
                whiteSpace: "pre-wrap",
                overflow: "auto",
                maxHeight: 160,
              }}
            >
              {this.state.error.name}: {this.state.error.message}
            </pre>
          </details>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: "#1a1a1a",
                color: "#fafaf7",
                border: "none",
                padding: "9px 16px",
                borderRadius: 8,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Reload page
            </button>
            <button
              onClick={() => {
                window.location.href = "/auth";
              }}
              style={{
                background: "#ffffff",
                color: "#1a1a1a",
                border: "1px solid #e5e5e0",
                padding: "9px 16px",
                borderRadius: 8,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Go to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }
}
